import { getMessage } from '@/localization.js';
import messages from './localization.json';

export class FormValidationController {
    constructor(host, options = {}) {
        this.host = host;
        this.getValue = options.getValue || (() => '');
        this.getInputRef = options.getInputRef || (() => null);
        /**
         * When set, `validate()` runs this first (expected to update `ElementInternals` via `setValidity`),
         * then derives `_isValid` and `_errorMessage` from `this._internals.validity` / `validationMessage`.
         * Used for controls that are not plain text inputs (e.g. file upload with `required` / `maxSize`).
         */
        this._customValidate = options.customValidate || null;
        this._errorMessage = '';
        this._isFirstValidity = true;
        this._isValid = true;

        if (!host._internals) {
            host._internals = host.attachInternals?.();
        }

        this._internals = host._internals;
    }

    _getHostForm() {
        return this.host?._internals?.form ?? this.host?.form ?? null;
    }

    getErrorMessage() {
        return this._errorMessage;
    }

    setInputHandlers() {
        this._getHostForm()?.addEventListener('submit', this._handleFormSubmit);
        this.host?.addEventListener('invalid', this._handleInvalid);
    }

    removeInputHandlers = () => {
        this._getHostForm()?.removeEventListener('submit', this._handleFormSubmit);
        this.host?.removeEventListener('invalid', this._handleInvalid);
    };

    setValidateState() {
        const isValid = this.validate();

        if (!isValid) {
            this.host.setAttribute('error', '');
            this.host.setAttribute('error-text', this._errorMessage);
        } else {
            this.host.removeAttribute('error');
            this.host.removeAttribute('error-text');
        }

        this.host.requestUpdate?.();
    }

    _handleInvalid = (e) => {
        e.preventDefault();
        this.setValidateState();

        // Only this host: other form-associated controls may still be out of sync in the same tick.
        if (!this._isValid) {
            this._focusTo(this.host);
        }
    };

    _focusTo(item) {
        if (!item) {
            return;
        }

        item?.focus({ preventScroll: false });
        const scrollOptions = { behavior: 'auto', block: 'start' };
        item.scrollIntoView(scrollOptions);
    }

    _handleFormSubmit = (e) => {
        if (!e.cancelable) {
            return;
        }

        this.setValidateState();

        // Each control registers its own listener; a form-wide :invalid / [error] query races other hosts
        // that have not run setValidateState yet in this submit dispatch.
        if (!this._isValid) {
            e.preventDefault();
            this._focusTo(this.host);
        }
    };

    _validateMinLength() {
        const inputRef = this.getInputRef();
        const minInputLength =
            !inputRef.value?.minLength || inputRef.value?.minLength === -1 ? 0 : inputRef.value?.minLength;

        if (inputRef.value?.value?.length >= minInputLength) {
            return true;
        }

        const errorMessage = getMessage(messages, 'invalidInputTooShort', {
            '#LENGTH#': inputRef.value?.minLength,
        });

        this._internals.setValidity({ tooShort: true }, errorMessage);
        this._errorMessage = errorMessage;
        return false;
    }

    _validateMaxLength() {
        const inputRef = this.getInputRef();
        const maxInputLength =
            !inputRef.value?.maxLength || inputRef.value?.maxLength === -1 ? Infinity : inputRef.value?.maxLength;

        if (inputRef.value?.value?.length <= maxInputLength) {
            return true;
        }

        const errorMessage = getMessage(messages, 'invalidInputTooLong', {
            '#LENGTH#': inputRef.value?.maxLength,
        });

        this._internals.setValidity({ tooLong: true }, errorMessage);
        this._errorMessage = errorMessage;

        return false;
    }

    validate() {
        if (!this._internals) {
            return (this._isValid = true);
        }

        if (this._customValidate) {
            this._customValidate();
            const valid = Boolean(this._internals.validity?.valid);
            this._errorMessage = valid ? '' : this._internals.validationMessage || '';
            return (this._isValid = valid);
        }

        const inputRef = this.getInputRef();

        if (!inputRef?.value) {
            return (this._isValid = true);
        }

        if (!inputRef.value.checkValidity()) {
            this._errorMessage = inputRef.value.validationMessage;
            this._internals.setValidity(inputRef.value.validity, this._errorMessage, inputRef?.value);

            return (this._isValid = false);
        }

        if (!this._validateMinLength()) {
            return (this._isValid = false);
        }

        if (!this._validateMaxLength()) {
            return (this._isValid = false);
        }

        this._errorMessage = '';
        this._internals.setValidity({});

        return (this._isValid = true);
    }
}
